import React, { useState } from "react";
import { FaCheckDouble, FaPeopleArrows } from "react-icons/fa";
import { Formik, Form } from "formik";
import moment from "moment";

import CardHeading from "../../../shared/CardHeading";
import CustomButton from "../../../shared/CustomButton";
import CustomTable from "../../../shared/CustomTable";
import { DeleteIcon, EditIcon } from "../../../utils/TableActionIcons";
import {
  citiesOptions,
  genderOptions,
  guarantorColumnsData,
  nationalitiesOptions,
} from "../../../constants/BeneficiaryConstants";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import DatePicker from "../../../shared/DatePicker";
import { guarantorValidationSchema } from "../../../validations/BeneficiaryFormValidations";
import { useTranslation } from "react-i18next";
import CheckPermission from "../../../hooks/CheckPermission";
const ReusableModal = React.lazy(() =>
  import("../../../shared/ReuseableModal")
);

const BeneFiciaryGuarantorForm = ({
  formValues,
  formikRef,
  openCreateGurantorModal,
  setOpenCreateGurantorModal,
}) => {
  const { hasPermission } = CheckPermission();
  const [selectedItemData, setSelectedItemData] = useState(null);
  const { t, i18n } = useTranslation();
  const actionsCoumn = {
    body: actionsTemplate,
    header: t("Actions"),
    headerCellClass: "!bg-gray-200",
  };
  const columnsData = [...guarantorColumnsData(t), actionsCoumn];

  function actionsTemplate(rowData, column) {
    return (
      <div className="flex items-center gap-1">
        {hasPermission('patient', 'update') && <EditIcon
          size={17}
          className="text-gray-500 mx-2 cursor-pointer"
          onClick={() => handleEditItem(rowData, column)}
        />}
        {hasPermission('patient', 'delete') && <DeleteIcon
          size={17}
          className="text-red-500 cursor-pointer"
          onClick={() => handleDeleteItem(rowData, column)}
        />}
      </div>
    );
  }

  // Handle Delete
  const handleDeleteItem = (rowData, column) => {
    let index = column?.rowIndex;
    const updatedContactsData = [...formikRef?.current?.values?.guarantors];
    updatedContactsData.splice(index, 1);
    formikRef?.current?.setFieldValue("guarantors", updatedContactsData);
  };

  //Handle Edit
  const handleEditItem = (rowData, column) => {
    let index = column?.rowIndex;
    const itemData = formValues?.guarantors[index];
    if (itemData) {
      itemData.index = index;
      setSelectedItemData(itemData);
      setOpenCreateGurantorModal(true);
    }
  };

  const handleOpenCreateModal = () => {
    setOpenCreateGurantorModal(true);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mt-2 mb-2">
        <CardHeading
          title={t("Guarantor")}
          Icon={FaPeopleArrows}
          iconSize={20}
        />
      </div>
      <div className="w-full mt-4 md:mt-6">
        <CustomTable columns={columnsData} data={formValues?.guarantors} />
      </div>
      <ReusableModal
        isVisible={openCreateGurantorModal}
        position="top"
        title={selectedItemData ? t("UpdateGuarantor") : t("AddNewGuarantor")}
        HeaderIcon={FaPeopleArrows}
        onClose={() => {
          setOpenCreateGurantorModal(false);
          setSelectedItemData(null);
        }}
      >
        <AddGuarantorForm
          formikRef={formikRef}
          setOpenCreateGurantorModal={setOpenCreateGurantorModal}
          selectedItemData={selectedItemData}
          setSelectedItemData={setSelectedItemData}
        />
      </ReusableModal>
    </div>
  );
};

const AddGuarantorForm = ({
  formikRef,
  setOpenCreateGurantorModal,
  setSelectedItemData,
  selectedItemData,
}) => {
  const { t, i18n } = useTranslation();
  const initialValues = {
    relation_with_patient: selectedItemData?.relation_with_patient || "",
    name: selectedItemData?.name || "",
    gender: selectedItemData?.gender || "",
    phone: selectedItemData?.phone || "",
    dob: selectedItemData?.dob
      ? moment(selectedItemData?.dob, [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "MM-DD-YYYY",
        "MM-DD-YY",
        "DD-MM-YY",
      ]).toDate()
      : "",
    address: selectedItemData?.address,
    nationality: selectedItemData?.nationality || "",
    city: selectedItemData?.city || "",
  };

  const handleFormSubmit = (values) => {
    let formatedValues = {
      ...values,
      dob: moment(values?.dob).format("DD/MM/YYYY"),
    };
    if (selectedItemData) {
      const updateData = formikRef?.current?.values?.guarantors.map(
        (data, index) =>
          index == selectedItemData?.index ? { ...formatedValues } : data
      );
      formikRef?.current?.setFieldValue("guarantors", updateData);
    } else {
      formikRef?.current?.setFieldValue("guarantors", [
        ...(formikRef?.current?.values?.guarantors || []),
        formatedValues,
      ]);
    }
    setOpenCreateGurantorModal(false);
    if (setSelectedItemData) {
      setSelectedItemData(null);
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={guarantorValidationSchema(t)}
        onSubmit={handleFormSubmit}
      >
        <Form>
          <div className="grid grid-cols-2  lg:grid-cols-3 gap-x-3 gap-y-1 mt-3">
            <TextInput
              label={t("RelationshipWithPatient*")}
              placeholder={t("Enterrelationship")}
              name="relation_with_patient"
            />
            <TextInput
              label={t("Name*")}
              placeholder={t("Entername")}
              name="name"
            />
            <DropdownField
              label={t("Gender")}
              options={genderOptions}
              placeholder={t("Selectgender")}
              optionLabel={i18n.language == "en" ? "label" : "labelAr"}
              optionValue={"value" }
              name="gender"
            />
            <TextInput
              label={t("PhoneNumber")}
              placeholder={t("Enterphonenumber")}
              name="phone"
            />
            <DatePicker
              label={t("DateOfBirth")}
              placeholder={t("Selectdateofbirth")}
              name="dob"
            />
            <TextInput
              label={t("Address")}
              placeholder={t("Enteraddress")}
              name="address"
            />
            <DropdownField
              label={t("City")}
              options={citiesOptions.map((opt) => ({
                ...opt,
                label: t(opt.label),
              }))}
              filter
              placeholder={t("Selectcity")}
              name="city"
            />
            <DropdownField
              label={t("Nationality")}
              options={nationalitiesOptions}
               optionValue={"value"}
              optionLabel={i18n.language == "en" ? "label_en" : "label_ar"}
              filter
              placeholder={t("Selectnationality")}
              name="nationality"
            />
          </div>
          <div className="flex items-center justify-end">
            <CustomButton
              icon={<FaCheckDouble size={12} className="me-2" />}
              type="submit"
              label={
                selectedItemData ? t("UpdateGuarantor") : t("AddNewGuarantor")
              }
              buttonType="primary"
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default BeneFiciaryGuarantorForm;
